import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Box,
  useToast,
  Image,
  Flex,
  Textarea,
  Text,
  SimpleGrid,
  VStack,
  Switch,
  HStack,
  Select,
  Checkbox,
  CheckboxGroup,
  Stack,
  Badge,
  Divider,
  Icon,
  NumberInput,
  NumberInputField
} from '@chakra-ui/react';
import { MdCloudUpload, MdAttachFile, MdImage } from 'react-icons/md';
import { campaignService } from '../../../../services/campaignService';
import { menuService } from '../../../../services/menuService';
import dayjs from 'dayjs';
import { cloudinaryDeliveryUrl } from '../../../../utils/cloudinaryDeliveryUrl';

const DAY_OPTIONS = [
  { value: 1, label: 'Pzt' },
  { value: 2, label: 'Sal' },
  { value: 3, label: 'Çar' },
  { value: 4, label: 'Per' },
  { value: 5, label: 'Cum' },
  { value: 6, label: 'Cmt' },
  { value: 0, label: 'Paz' }
];

const MAX_IMAGE_MB = 10;

function buildCampaignImageSrc(image) {
  if (!image || image instanceof File) return undefined;
  let apiUrl = import.meta.env.REACT_APP_API_URL || process.env.REACT_APP_API_URL || 'http://localhost:5001';
  if (apiUrl && !apiUrl.startsWith('http://') && !apiUrl.startsWith('https://')) {
    apiUrl = (process.env.NODE_ENV === 'production' ? 'https://' : 'http://') + apiUrl;
  }
  const full =
    typeof image === 'string' && (image.startsWith('http://') || image.startsWith('https://'))
      ? image
      : `${apiUrl}${typeof image === 'string' && image.startsWith('/') ? image : `/${image}`}`;
  return cloudinaryDeliveryUrl(full, 'wide1200');
}

function hasActiveMaxUsageLimit(value) {
  if (value === '' || value == null || value === undefined) return false;
  const n = Number(value);
  return !Number.isNaN(n) && n > 0;
}

function maxUsageInputValue(value) {
  if (value === '' || value == null || value === undefined) return '';
  const n = Number(value);
  return Number.isNaN(n) ? '' : String(value);
}

function mapCampaignToForm(campaign) {
  if (!campaign) return null;
  const recurrence = campaign.recurrence || {};
  return {
    campaignType: campaign.campaignType || 'coupon',
    discountType: campaign.discountType || 'percentage',
    discountValue: campaign.discountValue ?? campaign.discount ?? '',
    scheduleType: campaign.scheduleType || 'date_range',
    recurrence: {
      daysOfWeek: recurrence.daysOfWeek || [],
      allDay: recurrence.allDay !== false,
      startTime: recurrence.startTime || '09:00',
      endTime: recurrence.endTime || '17:45'
    },
    scopeType: campaign.scopeType || 'all',
    scopeCategoryIds: (campaign.scopeCategoryIds || []).map((id) => String(id)),
    scopeProductIds: (campaign.scopeProductIds || []).map((id) => String(id)),
    name: campaign.name || '',
    description: campaign.description || '',
    code: campaign.code || '',
    startDate: dayjs(campaign.startDate).format('YYYY-MM-DDTHH:mm'),
    endDate: dayjs(campaign.endDate).format('YYYY-MM-DDTHH:mm'),
    image: campaign.image || null,
    isActive: campaign.isActive !== false,
    isOneTimeUse: campaign.isOneTimeUse || false,
    maxUsageCount:
      campaign.maxUsageCount != null && campaign.maxUsageCount !== ''
        ? String(campaign.maxUsageCount)
        : '',
    minOrderAmount:
      campaign.minOrderAmount != null && campaign.minOrderAmount !== ''
        ? String(campaign.minOrderAmount)
        : ''
  };
}

export default function EditCampaignModal({ isOpen, onClose, campaign: initialCampaign }) {
  const [isLoading, setIsLoading] = useState(false);
  const [form, setForm] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const fileInputRef = useRef(null);
  const toast = useToast();

  const update = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  useEffect(() => {
    return () => {
      if (imagePreview && imagePreview.startsWith('blob:')) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [imagePreview]);

  useEffect(() => {
    if (!isOpen || !initialCampaign) return;
    setForm(mapCampaignToForm(initialCampaign));
    setImagePreview(buildCampaignImageSrc(initialCampaign.image) || null);
    setIsDragOver(false);

    const loadMenu = async () => {
      try {
        const [cats, prods] = await Promise.all([
          menuService.getCategories(),
          menuService.getProducts()
        ]);
        setCategories(Array.isArray(cats) ? cats : cats?.data || []);
        setProducts(Array.isArray(prods) ? prods : prods?.data || []);
      } catch (e) {
        console.warn('Menü verisi yüklenemedi', e);
      }
    };
    loadMenu();
  }, [isOpen, initialCampaign]);

  const handleImageFile = useCallback(
    (file) => {
      if (!file) return;
      if (!file.type.startsWith('image/')) {
        toast({ title: 'Geçersiz dosya', status: 'warning', duration: 3000 });
        return;
      }
      if (file.size > MAX_IMAGE_MB * 1024 * 1024) {
        toast({
          title: `Maksimum ${MAX_IMAGE_MB}MB`,
          status: 'warning',
          duration: 3000
        });
        return;
      }
      setImagePreview((prev) => {
        if (prev?.startsWith('blob:')) URL.revokeObjectURL(prev);
        return URL.createObjectURL(file);
      });
      update({ image: file });
    },
    [toast]
  );

  const openFilePicker = () => fileInputRef.current?.click();

  const validate = () => {
    if (!form.name?.trim()) return 'Kampanya adı gerekli';
    if (!form.description?.trim()) return 'Açıklama gerekli';
    if (!form.startDate || !form.endDate) return 'Tarihler gerekli';
    const v = Number(form.discountValue);
    if (Number.isNaN(v)) return 'İndirim değeri gerekli';
    if (form.discountType === 'percentage' && (v < 0 || v > 100)) {
      return 'Yüzde 0-100 arasında olmalı';
    }
    if (form.discountType !== 'percentage' && v <= 0) return 'Geçerli indirim tutarı girin';
    if (form.campaignType === 'coupon' && !form.code?.trim()) return 'Kupon kodu gerekli';
    if (form.scheduleType === 'recurring' && !form.recurrence.daysOfWeek?.length) {
      return 'Tekrarlayan kampanya için gün seçin';
    }
    if (form.scopeType === 'categories' && !form.scopeCategoryIds.length) {
      return 'En az bir kategori seçin';
    }
    if (form.scopeType === 'products' && !form.scopeProductIds.length) {
      return 'En az bir ürün seçin';
    }
    if (!form.image) return 'Kampanya görseli gerekli';
    return null;
  };

  const handleSubmit = async () => {
    const err = validate();
    if (err) {
      toast({ title: err, status: 'warning', duration: 3000 });
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim(),
        campaignType: form.campaignType,
        discountType: form.discountType,
        discountValue: Number(form.discountValue),
        discount: form.discountType === 'percentage' ? Number(form.discountValue) : 0,
        scheduleType: form.scheduleType,
        recurrence: form.recurrence,
        scopeType: form.scopeType,
        scopeCategoryIds: form.scopeCategoryIds,
        scopeProductIds: form.scopeProductIds,
        applyToMenu: form.campaignType === 'automatic',
        startDate: form.startDate,
        endDate: form.endDate,
        isActive: form.isActive,
        isOneTimeUse: form.isOneTimeUse,
        maxUsageCount: hasActiveMaxUsageLimit(form.maxUsageCount)
          ? Number(form.maxUsageCount)
          : '',
        minOrderAmount:
          form.minOrderAmount !== '' && form.minOrderAmount != null
            ? Number(form.minOrderAmount)
            : ''
      };

      if (form.campaignType === 'coupon') {
        payload.code = form.code.trim().toUpperCase();
      }

      if (form.image instanceof File) {
        payload.image = form.image;
      }

      await campaignService.updateCampaign(initialCampaign._id, payload);

      toast({
        title: 'Başarılı',
        description: 'Kampanya güncellendi.',
        status: 'success',
        duration: 3000
      });
      onClose();
    } catch (error) {
      toast({
        title: 'Hata',
        description: error.response?.data?.message || error.message,
        status: 'error',
        duration: 4000
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!form) return null;

  const isAutomatic = form.campaignType === 'automatic';
  const hasMaxLimit = hasActiveMaxUsageLimit(form.maxUsageCount);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" scrollBehavior="inside">
      <ModalOverlay
        bg="blackAlpha.500"
        backdropFilter="blur(10px)"
        sx={{ WebkitBackdropFilter: 'blur(10px)' }}
      />
      <ModalContent maxH="90vh">
        <ModalHeader borderBottomWidth="1px">
          <Flex align="center" gap={2} flexWrap="wrap">
            <Text>Kampanya Düzenle</Text>
            <Badge colorScheme={isAutomatic ? 'purple' : 'green'}>
              {isAutomatic ? 'Otomatik' : 'Kuponlu'}
            </Badge>
          </Flex>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody py={5}>
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
            {/* Görsel */}
            <VStack align="stretch" spacing={3}>
              <FormControl>
                <FormLabel fontWeight="bold">Kampanya görseli</FormLabel>
                <Input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/webp"
                  display="none"
                  onChange={(e) => handleImageFile(e.target.files?.[0])}
                />
                {form.image instanceof File ? (
                  <Box p={4} borderRadius="12px" bg="gray.50" border="2px dashed" borderColor="#e45858">
                    <Flex align="center" justify="space-between" mb={3}>
                      <Flex align="center" gap={3} minW={0}>
                        <Icon as={MdAttachFile} color="#e45858" w={6} h={6} />
                        <Box minW={0}>
                          <Text fontSize="sm" fontWeight="600" noOfLines={1}>
                            {form.image.name}
                          </Text>
                          <Text fontSize="xs" color="gray.600">
                            {(form.image.size / 1024 / 1024).toFixed(2)} MB
                          </Text>
                        </Box>
                      </Flex>
                    </Flex>
                    <Button
                      size="sm"
                      w="full"
                      variant="outline"
                      colorScheme="red"
                      leftIcon={<Icon as={MdCloudUpload} />}
                      onClick={openFilePicker}
                    >
                      Başka görsel seç
                    </Button>
                  </Box>
                ) : (
                  <Box
                    as="button"
                    type="button"
                    onClick={openFilePicker}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setIsDragOver(true);
                    }}
                    onDragLeave={() => setIsDragOver(false)}
                    onDrop={(e) => {
                      e.preventDefault();
                      setIsDragOver(false);
                      handleImageFile(e.dataTransfer.files?.[0]);
                    }}
                    w="100%"
                    p={5}
                    borderRadius="12px"
                    bg={isDragOver ? 'red.50' : 'gray.50'}
                    border="2px dashed"
                    borderColor={isDragOver ? '#e45858' : 'gray.300'}
                    _hover={{ borderColor: '#e45858', bg: 'gray.100' }}
                    transition="all 0.2s"
                  >
                    <Flex direction="column" align="center" gap={1}>
                      <Icon as={MdCloudUpload} color="#e45858" w={8} h={8} />
                      <Text fontSize="sm" fontWeight="600">
                        Yeni görsel seç veya sürükle
                      </Text>
                    </Flex>
                  </Box>
                )}
              </FormControl>

              <Box
                borderRadius="16px"
                overflow="hidden"
                bg="gray.100"
                border="1px solid"
                borderColor="gray.200"
                minH="280px"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                {imagePreview ? (
                  <Image
                    src={imagePreview}
                    alt="Önizleme"
                    w="100%"
                    minH="280px"
                    objectFit="cover"
                  />
                ) : (
                  <VStack color="gray.500" p={4}>
                    <Icon as={MdImage} w={10} h={10} opacity={0.4} />
                    <Text fontSize="sm">Önizleme</Text>
                  </VStack>
                )}
              </Box>
            </VStack>

            {/* Form */}
            <VStack align="stretch" spacing={5}>
              <Box>
                <Text fontSize="sm" fontWeight="bold" color="gray.700" mb={3}>
                  İndirim
                </Text>
                <SimpleGrid columns={2} spacing={3}>
                  <FormControl>
                    <FormLabel fontSize="sm">İndirim tipi</FormLabel>
                    <Select
                      size="sm"
                      value={form.discountType}
                      onChange={(e) => update({ discountType: e.target.value })}
                    >
                      <option value="percentage">Yüzde (%)</option>
                      <option value="fixed_amount">Sabit tutar (₺)</option>
                      <option value="fixed_price">Sabit fiyat (₺)</option>
                    </Select>
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">
                      {form.discountType === 'percentage'
                        ? 'Oran (%)'
                        : form.discountType === 'fixed_amount'
                          ? 'İndirim (₺)'
                          : 'Fiyat (₺)'}
                    </FormLabel>
                    <NumberInput
                      size="sm"
                      value={form.discountValue}
                      onChange={(_, v) => update({ discountValue: v })}
                      min={0}
                      max={form.discountType === 'percentage' ? 100 : undefined}
                    >
                      <NumberInputField />
                    </NumberInput>
                  </FormControl>
                </SimpleGrid>
              </Box>

              <Divider />

              <Box>
                <Text fontSize="sm" fontWeight="bold" color="gray.700" mb={3}>
                  Zamanlama
                </Text>
                <Stack spacing={3}>
                  <FormControl>
                    <FormLabel fontSize="sm">Tür</FormLabel>
                    <Select
                      size="sm"
                      value={form.scheduleType}
                      onChange={(e) => update({ scheduleType: e.target.value })}
                    >
                      <option value="date_range">Tarih aralığı</option>
                      <option value="recurring">Tekrarlayan (gün/saat)</option>
                    </Select>
                  </FormControl>
                  {form.scheduleType === 'recurring' && (
                    <>
                      <FormControl>
                        <FormLabel fontSize="sm">Günler</FormLabel>
                        <CheckboxGroup
                          value={form.recurrence.daysOfWeek.map(String)}
                          onChange={(vals) =>
                            update({
                              recurrence: {
                                ...form.recurrence,
                                daysOfWeek: vals.map(Number)
                              }
                            })
                          }
                        >
                          <Flex wrap="wrap" gap={2}>
                            {DAY_OPTIONS.map((d) => (
                              <Checkbox key={d.value} value={String(d.value)} size="sm">
                                {d.label}
                              </Checkbox>
                            ))}
                          </Flex>
                        </CheckboxGroup>
                      </FormControl>
                      <HStack>
                        <FormControl display="flex" alignItems="center">
                          <FormLabel fontSize="sm" mb={0} mr={2}>
                            Tüm gün
                          </FormLabel>
                          <Switch
                            size="sm"
                            isChecked={form.recurrence.allDay}
                            onChange={(e) =>
                              update({
                                recurrence: { ...form.recurrence, allDay: e.target.checked }
                              })
                            }
                          />
                        </FormControl>
                      </HStack>
                      {!form.recurrence.allDay && (
                        <SimpleGrid columns={2} spacing={3}>
                          <FormControl>
                            <FormLabel fontSize="sm">Başlangıç</FormLabel>
                            <Input
                              size="sm"
                              type="time"
                              value={form.recurrence.startTime}
                              onChange={(e) =>
                                update({
                                  recurrence: { ...form.recurrence, startTime: e.target.value }
                                })
                              }
                            />
                          </FormControl>
                          <FormControl>
                            <FormLabel fontSize="sm">Bitiş</FormLabel>
                            <Input
                              size="sm"
                              type="time"
                              value={form.recurrence.endTime}
                              onChange={(e) =>
                                update({
                                  recurrence: { ...form.recurrence, endTime: e.target.value }
                                })
                              }
                            />
                          </FormControl>
                        </SimpleGrid>
                      )}
                    </>
                  )}
                  <SimpleGrid columns={2} spacing={3}>
                    <FormControl isRequired>
                      <FormLabel fontSize="sm">Başlangıç</FormLabel>
                      <Input
                        size="sm"
                        type="datetime-local"
                        value={form.startDate}
                        onChange={(e) => update({ startDate: e.target.value })}
                      />
                    </FormControl>
                    <FormControl isRequired>
                      <FormLabel fontSize="sm">Bitiş</FormLabel>
                      <Input
                        size="sm"
                        type="datetime-local"
                        value={form.endDate}
                        onChange={(e) => update({ endDate: e.target.value })}
                      />
                    </FormControl>
                  </SimpleGrid>
                </Stack>
              </Box>

              <Divider />

              <Box>
                <Text fontSize="sm" fontWeight="bold" color="gray.700" mb={3}>
                  Kapsam
                </Text>
                <FormControl mb={3}>
                  <FormLabel fontSize="sm">Uygulama alanı</FormLabel>
                  <Select
                    size="sm"
                    value={form.scopeType}
                    onChange={(e) =>
                      update({
                        scopeType: e.target.value,
                        scopeCategoryIds: [],
                        scopeProductIds: []
                      })
                    }
                  >
                    <option value="all">Tüm menü</option>
                    <option value="categories">Seçili kategoriler</option>
                    <option value="products">Seçili ürünler</option>
                  </Select>
                </FormControl>
                {form.scopeType === 'categories' && (
                  <CheckboxGroup
                    value={form.scopeCategoryIds}
                    onChange={(vals) => update({ scopeCategoryIds: vals })}
                  >
                    <Stack maxH="120px" overflowY="auto" spacing={1}>
                      {categories.map((c) => (
                        <Checkbox key={c._id} value={c._id} size="sm">
                          {c.name}
                        </Checkbox>
                      ))}
                    </Stack>
                  </CheckboxGroup>
                )}
                {form.scopeType === 'products' && (
                  <CheckboxGroup
                    value={form.scopeProductIds}
                    onChange={(vals) => update({ scopeProductIds: vals })}
                  >
                    <Stack maxH="120px" overflowY="auto" spacing={1}>
                      {products.map((p) => (
                        <Checkbox key={p._id} value={p._id} size="sm">
                          {p.name}
                        </Checkbox>
                      ))}
                    </Stack>
                  </CheckboxGroup>
                )}
              </Box>

              <Divider />

              <Box>
                <Text fontSize="sm" fontWeight="bold" color="gray.700" mb={3}>
                  Genel bilgiler
                </Text>
                <Stack spacing={3}>
                  <FormControl isRequired>
                    <FormLabel fontSize="sm">Kampanya adı</FormLabel>
                    <Input
                      size="sm"
                      value={form.name}
                      onChange={(e) => update({ name: e.target.value })}
                    />
                  </FormControl>
                  <FormControl isRequired>
                    <FormLabel fontSize="sm">Açıklama</FormLabel>
                    <Textarea
                      size="sm"
                      rows={2}
                      value={form.description}
                      onChange={(e) => update({ description: e.target.value })}
                    />
                  </FormControl>
                  {!isAutomatic && (
                    <FormControl isRequired>
                      <FormLabel fontSize="sm">Kupon kodu</FormLabel>
                      <Input
                        size="sm"
                        value={form.code}
                        onChange={(e) => update({ code: e.target.value.toUpperCase() })}
                      />
                    </FormControl>
                  )}
                  {isAutomatic && (
                    <Box p={3} bg="purple.50" borderRadius="md" fontSize="sm" color="purple.800">
                      Otomatik kampanya — kod yok; indirim menüde uygulanır.
                    </Box>
                  )}
                </Stack>
              </Box>

              <Divider />

              <Box>
                <Text fontSize="sm" fontWeight="bold" color="gray.700" mb={3}>
                  Kullanım limitleri
                </Text>
                <Stack spacing={3}>
                  <FormControl>
                    <FormLabel fontSize="sm">Kampanya durumu</FormLabel>
                    <HStack>
                      <Switch
                        isChecked={form.isActive}
                        onChange={(e) => update({ isActive: e.target.checked })}
                      />
                      <Text fontSize="sm" color="gray.600">
                        {form.isActive ? 'Aktif' : 'Pasif'}
                      </Text>
                    </HStack>
                  </FormControl>

                  <FormControl>
                    <FormLabel fontSize="sm">Minimum sepet tutarı (₺)</FormLabel>
                    <Input
                      size="sm"
                      type="number"
                      min={0}
                      placeholder="Yok"
                      value={form.minOrderAmount}
                      onChange={(e) => update({ minOrderAmount: e.target.value })}
                    />
                    <Text fontSize="xs" color="gray.500" mt={1}>
                      Boş = minimum yok. Kupon veya otomatik kampanya için sepet alt limiti.
                    </Text>
                  </FormControl>

                  <FormControl>
                    <FormLabel fontSize="sm">Maks. toplam kullanım</FormLabel>
                    <Input
                      size="sm"
                      type="number"
                      min={1}
                      placeholder="Sınırsız"
                      value={maxUsageInputValue(form.maxUsageCount)}
                      onChange={(e) => {
                        const v = e.target.value;
                        update({ maxUsageCount: v === '' ? '' : v });
                      }}
                    />
                    <Text fontSize="xs" color="gray.500" mt={1}>
                      {hasMaxLimit
                        ? `Tüm müşteriler toplamda en fazla ${form.maxUsageCount} kez. Şu an: ${initialCampaign?.currentUsageCount || 0} kullanım.`
                        : 'Boş bırakın = sınırsız toplam kullanım.'}
                    </Text>
                  </FormControl>

                  <FormControl>
                    <FormLabel fontSize="sm">Tek kullanımlık (kullanıcı başına)</FormLabel>
                    <HStack align="start">
                      <Switch
                        isChecked={form.isOneTimeUse}
                        onChange={(e) => update({ isOneTimeUse: e.target.checked })}
                      />
                      <Text fontSize="xs" color="gray.600" lineHeight="tall">
                        {form.isOneTimeUse
                          ? 'Her müşteri bu kampanyayı yalnızca bir kez kullanır.'
                          : 'Aynı müşteri kampanya süresince tekrar kullanabilir.'}
                      </Text>
                    </HStack>
                  </FormControl>
                </Stack>
              </Box>
            </VStack>
          </SimpleGrid>
        </ModalBody>

        <ModalFooter borderTopWidth="1px">
          <Button variant="ghost" mr={3} onClick={onClose} isDisabled={isLoading}>
            İptal
          </Button>
          <Button colorScheme="red" onClick={handleSubmit} isLoading={isLoading}>
            Güncelle
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
